import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StickyHeaderLayout } from '@components/StickyHeaderLayout';
import { ClaimEdit } from '@features/billing/components/Claim';
import { useGetClaimByIdQuery, useLazyGetClaimPdfQuery } from '@features/billing/store';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, CircularProgress, Container, Stack, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';

export const ClaimView: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { claimId } = useParams<{ claimId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  
  // Fetch claim data to check status
  const { data: claim } = useGetClaimByIdQuery(
    { claimId: claimId || '' },
    { skip: !claimId }
  );
  
  const [getClaimPdf, { isLoading: isDownloading }] = useLazyGetClaimPdfQuery();

  const handleDownloadPDF = async () => {
    if (!claimId) return;
    
    try {
      const result = await getClaimPdf({ claimId }).unwrap();
      
      // Create blob from base64 data
      const byteCharacters = atob(result.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename && !result.filename.includes('None') 
        ? result.filename 
        : `claim_${claimId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  // Action buttons for the header
  const headerActions = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ flexShrink: 0 }}
    >
      {claim?.status === 'finalized' && (
        <Button
          variant="contained"
          color="primary"
          startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={isDownloading}
        >
          {isDownloading ? 'Downloading...' : 'Download PDF'}
        </Button>
      )}
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100%', backgroundColor: theme.palette.background.default }}>
      <StickyHeaderLayout
        title="View Claim"
        subtitle="CMS-1500 Health Insurance Claim Form (Read-Only)"
        backButton={{
          label: 'Back to Claims',
          onClick: () => navigate('/billing?tab=claims'),
        }}
        actions={headerActions}
        containerId="claim-view"
      >
        <Container maxWidth="xl" sx={{ pb: 3 }}>
          <ClaimEdit readOnly />
        </Container>
      </StickyHeaderLayout>
    </Box>
  );
};
